import { useCallback, useEffect, useRef } from "react";
import { keyboardInset } from "@acto/taskpane/field-sales-2/utils/visualViewport";

/**
 * Returns a callback ref for a bottom-anchored surface that keeps its `--keyboard-inset` custom
 * property in sync with how far the on-screen keyboard overlaps the bottom of the (layout)
 * viewport — 0 when closed. The surface reads the var to lift itself above the keyboard:
 * `position: fixed` anchors to the layout viewport, which iOS never shrinks for the keyboard, so
 * without this the keyboard draws straight over the card.
 *
 * The subscription is bound to the DOM node's lifecycle (via the returned ref), not captured once
 * in an effect, so it re-attaches cleanly every time Radix mounts/reuses the sheet across its
 * open/close animation — otherwise rapid open/close toggling desyncs the listener from the live
 * node and the lift only works on alternating opens. Geometry is read live on each visualViewport
 * frame (coalesced through rAF) so the sheet rides along with the keyboard instead of trailing a
 * React render behind. SSR-safe (no-op with no window/viewport).
 */
export function useKeyboardInset(enabled: boolean): (node: HTMLElement | null) => void {
  const nodeRef = useRef<HTMLElement | null>(null);
  const frameRef = useRef(0);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const apply = useCallback(() => {
    const node = nodeRef.current;
    const viewport = typeof window === "undefined" ? undefined : window.visualViewport;
    if (!node || !viewport) return;

    const inset = enabledRef.current ? keyboardInset(viewport) : 0;
    node.style.setProperty("--keyboard-inset", `${inset}px`);
  }, []);

  useEffect(() => {
    const viewport = typeof window === "undefined" ? undefined : window.visualViewport;
    if (!enabled || !viewport) return;

    const schedule = () => {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(apply);
    };

    apply();
    viewport.addEventListener("resize", schedule);
    viewport.addEventListener("scroll", schedule);

    return () => {
      cancelAnimationFrame(frameRef.current);
      viewport.removeEventListener("resize", schedule);
      viewport.removeEventListener("scroll", schedule);
    };
  }, [enabled, apply]);

  return useCallback(
    (node: HTMLElement | null) => {
      nodeRef.current = node;
      // A freshly mounted (or Radix-reused) node starts with no inset; apply immediately so the
      // very first open lifts without waiting for a viewport event that may not fire.
      apply();
    },
    [apply],
  );
}
